import * as React from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { Checkbox } from "../../../../components/ui/checkbox";
import { Label } from "../../../../components/ui/label";
import { FieldError } from "../../../runQuote/components/FieldError";
import { Producer } from "../../../runQuote/components/intialInformation/Producer";
import { AgentEmail } from "../../../runQuote/components/intialInformation/AgentEmail";
import { PolicyEffectiveDate } from "../../../runQuote/components/intialInformation/PolicyEffectiveDate";
import { normalizeProducer } from "../../../runQuote/components/intialInformation/normalizeProducer";
import { InsuredState } from "../InsuredState";

const CARRIERS = [
  { id: "Progressive", label: "Progressive", sub: "1-2 Business Days" },
];

export function InitialInfo({ user, producers }) {
  const { control, setValue, getValues } = useFormContext();

  const allowedCarriers = useWatch({ control, name: "step1.allowedCarriers" });
  const carriers = useWatch({ control, name: "step1.carrier" });

  const [openPolicyDate, setOpenPolicyDate] = React.useState(false);

  const producerOptions = React.useMemo(() => {
    if (producers.loading || !producers.data?.users) return [];
    return producers.data.users.map((u) => ({
      value: String(u.id),
      label: u.name,
      email: u.email,
    }));
  }, [producers.loading, producers.data]);

  React.useEffect(() => {
    if (user.loading || !user.data || !producerOptions.length) return;

    const current = getValues("step1.producer");
    const normalized = normalizeProducer(current);

    if (current && current !== normalized) {
      setValue("step1.producer", normalized, {
        shouldDirty: false,
        shouldValidate: true,
      });
      return;
    }

    if (!normalized) {
      const match = producerOptions.find((u) => u.value === String(user.data.id));
      if (match) {
        setValue("step1.producer", normalizeProducer(match.value), {
          shouldDirty: false,
          shouldValidate: true,
        });
      }
    }
  }, [user.loading, user.data, producerOptions, setValue, getValues]);

  React.useEffect(() => {
    const d = getValues("step1.policyDate");
    if (!d) {
      setValue("step1.policyDate", new Date(), {
        shouldDirty: false,
        shouldValidate: true,
      });
    }
  }, [getValues, setValue]);

  React.useEffect(() => {
    if (!Array.isArray(allowedCarriers) || !Array.isArray(carriers)) return;
    if (allowedCarriers.length === 0) {
      if (carriers.length) {
        setValue("step1.carrier", [], { shouldValidate: false });
      }
      return;
    }
    const filtered = carriers.filter((c) => allowedCarriers.includes(c));
    if (JSON.stringify(filtered) !== JSON.stringify(carriers)) {
      setValue("step1.carrier", filtered, { shouldValidate: true });
    }
  }, [allowedCarriers, carriers, setValue]);

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold">Initial Information</h2>

      <Producer
        control={control}
        producers={producers}
        producerOptions={producerOptions}
      />

      <AgentEmail control={control} producers={producers} />

      <PolicyEffectiveDate control={control}
        openPolicyDate={openPolicyDate}
        setOpenPolicyDate={setOpenPolicyDate}
      />

      <InsuredState control={control} producers={producers} setValue={setValue} />

      <div className="space-y-3">
        {allowedCarriers?.length ? (
          <>
            <Label>Select from the below carriers:</Label>
            <div className="flex flex-wrap gap-8">
              {CARRIERS.filter((c) => allowedCarriers.includes(c.id)).map((c) => {
                const itemId = `personal-carrier-${c.id}`;
                const isChecked = carriers?.includes(c.id);
                return (
                  <div key={c.id} className="flex items-start gap-2">
                    <Checkbox
                      id={itemId}
                      checked={isChecked}
                      onCheckedChange={() => {
                        const next = isChecked
                          ? carriers.filter((v) => v !== c.id)
                          : [...(carriers || []), c.id];
                        setValue("step1.carrier", next, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      }}
                      className={[
                        "data-[state=checked]:!bg-transparent data-[state=checked]:!text-inherit",
                        "data-[state=checked]:border-black",
                        "[&>span[data-slot='checkbox-indicator']]:hidden",
                        "[&>span[data-slot='checkbox-indicator']>svg]:hidden",
                        "after:content-[''] after:absolute after:top-[1px] after:left-[1px]",
                        "after:h-3 after:w-3 after:rounded-[3px] after:bg-red-600",
                        "after:opacity-0 data-[state=checked]:after:opacity-100",
                        "relative h-4 w-4 shrink-0 rounded-[4px] border border-gray-300",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-red-600",
                      ].join(" ")}
                    />
                    <Label htmlFor={itemId} className="cursor-pointer select-none leading-tight">
                      <div className="font-medium">{c.label}</div>
                      <div className="text-xs text-gray-500">{c.sub}</div>
                    </Label>
                  </div>
                );
              })}
            </div>
            <FieldError name="step1.carrier" step="step1" control={control} />
          </>
        ) : (
          <div className="text-sm text-gray-500">
            Please select an insured state to see available carriers.
          </div>
        )}
      </div>
    </div>
  );
}
